import type { PermissionPage } from '@/services/permissions'
import type { PageKey } from '@/utils/pagePermissions'

export type NavSection = 'Payables' | 'Books' | 'Members' | 'Financials' | 'Settings'

export type NavPageConfig = {
  pageKey: PageKey
  label: string
  section: NavSection
  defaultPath: string
}

export const NAV_PAGES: NavPageConfig[] = [
  {
    pageKey: 'invoices',
    label: 'Invoices',
    section: 'Payables',
    defaultPath: '/bookkeeper/doc-review',
  },
  {
    pageKey: 'purchasing',
    label: 'Purchasing',
    section: 'Payables',
    defaultPath: '/purchasing/review',
  },
  {
    pageKey: 'vendors',
    label: 'Vendors',
    section: 'Payables',
    defaultPath: '/vendor-details',
  },
  {
    pageKey: 'transactions',
    label: 'Transactions',
    section: 'Payables',
    defaultPath: '/qbo-transaction-search',
  },
  {
    pageKey: 'gcp',
    label: 'GCP',
    section: 'Payables',
    defaultPath: '/gcp',
  },
  {
    pageKey: 'gift-cards',
    label: 'Gift Cards',
    section: 'Payables',
    defaultPath: '/gift-cards',
  },
  {
    pageKey: 'clover-reports',
    label: 'Clover',
    section: 'Books',
    defaultPath: '/clover',
  },
  {
    pageKey: 'warehouse',
    label: 'Warehouse',
    section: 'Books',
    defaultPath: '/warehouse',
  },
  {
    pageKey: 'payroll',
    label: 'Payroll',
    section: 'Books',
    defaultPath: '/payroll',
  },
  {
    pageKey: 'benefits',
    label: 'Benefits',
    section: 'Books',
    defaultPath: '/benefits',
  },
  {
    pageKey: 'fixed-assets',
    label: 'Fixed Assets',
    section: 'Books',
    defaultPath: '/fixed-assets/allocate',
  },
  {
    pageKey: 'bank-recon',
    label: 'Bank Recon',
    section: 'Books',
    defaultPath: '/bank-recon',
  },
  {
    pageKey: 'member-ledger',
    label: 'Member Ledger',
    section: 'Members',
    defaultPath: '/member-ledger/unit-ledger',
  },
  {
    pageKey: 'hp-threshold',
    label: 'HP Threshold',
    section: 'Members',
    defaultPath: '/hp-threshold/balances',
  },
  {
    pageKey: 'preferred-equity',
    label: 'Preferred Equity',
    section: 'Members',
    defaultPath: '/preferred-equity',
  },
  {
    pageKey: 'distributions',
    label: 'Distributions & Income',
    section: 'Members',
    defaultPath: '/distribution-waterfall/calculator',
  },
  {
    pageKey: 'monthly-performance',
    label: 'Reporting',
    section: 'Financials',
    defaultPath: '/reporting/report',
  },
  {
    pageKey: 'projections',
    label: 'Projections',
    section: 'Financials',
    defaultPath: '/projections',
  },
  {
    pageKey: 'settings',
    label: 'Settings',
    section: 'Settings',
    defaultPath: '/settings',
  },
]

export type PageSectionGroup = {
  section: string
  pages: PermissionPage[]
}

export function groupPermissionPagesByNav(pages: PermissionPage[]): PageSectionGroup[] {
  const pageByKey = new Map(pages.map((page) => [page.key, page]))
  const usedKeys = new Set<string>()
  const groups: PageSectionGroup[] = []

  let currentSection: string | null = null
  let currentPages: PermissionPage[] = []

  const flush = () => {
    if (currentSection !== null && currentPages.length > 0) {
      groups.push({ section: currentSection, pages: currentPages })
    }
    currentPages = []
  }

  for (const navPage of NAV_PAGES) {
    const page = pageByKey.get(navPage.pageKey)
    if (!page) continue
    usedKeys.add(page.key)
    const section = page.section || navPage.section
    if (section !== currentSection) {
      flush()
      currentSection = section
    }
    currentPages.push(page)
  }
  flush()

  for (const page of pages) {
    if (usedKeys.has(page.key)) continue
    const section = page.section || 'Other'
    const last = groups[groups.length - 1]
    if (last && last.section === section) {
      last.pages.push(page)
    } else {
      groups.push({ section, pages: [page] })
    }
  }

  return groups
}

export function navPagesForSection(section: NavSection): NavPageConfig[] {
  return NAV_PAGES.filter((page) => page.section === section)
}
